import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { i18nAPI } from '../services/api';

/**
 * useUITranslations
 *
 * On mount (and when locale changes), fetches published UI string overrides
 * from the backend and merges them on top of the bundled JSON via
 * i18next.addResourceBundle(). This allows translator corrections to take
 * effect immediately without a code deploy.
 *
 * Should be called ONCE near the app root (e.g., App.jsx).
 */
export function useUITranslations() {
  const { i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const fetchedRef = useRef({});

  useEffect(() => {
    // No overrides needed for English (canonical source)
    if (locale === 'en') return;

    // Don't re-fetch if we already fetched for this locale
    if (fetchedRef.current[locale]) return;

    i18nAPI
      .getUIStrings(locale)
      .then((res) => {
        const overrides = res.data?.overrides;
        if (overrides && typeof overrides === 'object' && Object.keys(overrides).length > 0) {
          // deep=true, overwrite=true — merges overrides on top of bundled JSON
          i18n.addResourceBundle(locale, 'translation', overrides, true, true);
          fetchedRef.current[locale] = true;
        }
      })
      .catch(() => {
        // Silent — bundled JSON is the fallback, this is purely additive
      });
  }, [locale, i18n]);
}
